import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { lumi } from '../lib/lumi'
import { Calendar, Edit, FileText, Filter, KeyRound, Mail, Phone, Plus, RotateCcw, Search, Trash2, Upload, UserCheck, UserX, X } from 'lucide-react'
import toast from 'react-hot-toast'
import { DEFAULT_LOCAL_PASSWORD, createLocalCredentials, normalizeEmail } from '../lib/appSession'

interface Researcher {
  _id: string
  name: string
  email: string
  phone?: string
  department?: string
  specialization?: string
  photoUrl?: string
  isActive: boolean
  assignedSurveys: number
  completedSurveys: number
  createdAt: string
  updatedAt: string
  authProvider?: 'local' | 'google'
  googleLinked?: boolean
  passwordHash?: string
  passwordSalt?: string
  mustResetPassword?: boolean
  passwordUpdatedAt?: string
}

const sortResearchersByName = (items: Researcher[]) => (
  [...items].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' }))
)

const ResearcherManager: React.FC = () => {
  const [researchers, setResearchers] = useState<Researcher[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [researchersPage, setResearchersPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingResearcher, setEditingResearcher] = useState<Researcher | null>(null)

  const ensureLocalResearcherAccess = useCallback(async (researcher: Researcher): Promise<Researcher> => {
    const needsPassword = !researcher.passwordHash || !researcher.passwordSalt
    const needsUnlink = researcher.authProvider !== 'local' || researcher.googleLinked !== false

    if (!needsPassword && !needsUnlink) {
      return researcher
    }

    const credentials = needsPassword
      ? await createLocalCredentials(DEFAULT_LOCAL_PASSWORD, { mustResetPassword: true })
      : {}

    const updatedResearcher = await lumi.entities.researchers.update(researcher._id, {
      ...credentials,
      authProvider: 'local',
      googleLinked: false,
      updatedAt: new Date().toISOString(),
    }) as Researcher

    return { ...researcher, ...updatedResearcher }
  }, [])

  const loadResearchers = useCallback(async () => {
    try {
      setLoading(true)
      const response = await lumi.entities.researchers.list({
        sort: { name: 1 },
      })
      const list = (response.list || []) as Researcher[]
      const normalizedResearchers = await Promise.all(list.map(ensureLocalResearcherAccess))
      setResearchers(sortResearchersByName(normalizedResearchers))
    } catch (error) {
      console.error('Erro ao carregar entrevistadores:', error)
      toast.error('Erro ao carregar entrevistadores')
    } finally {
      setLoading(false)
    }
  }, [ensureLocalResearcherAccess])

  useEffect(() => {
    loadResearchers()
  }, [loadResearchers])

  const handleCreateResearcher = async (researcherData: Partial<Researcher>) => {
    try {
      const credentials = await createLocalCredentials(DEFAULT_LOCAL_PASSWORD, {
        mustResetPassword: true,
      })
      const newResearcher = await lumi.entities.researchers.create({
        ...researcherData,
        email: normalizeEmail(researcherData.email || ''),
        ...credentials,
        isActive: true,
        assignedSurveys: 0,
        completedSurveys: 0,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      }) as Researcher

      setResearchers((prev) => sortResearchersByName([newResearcher, ...prev]))
      setShowCreateModal(false)
      toast.success(`Entrevistador cadastrado. Senha inicial: ${DEFAULT_LOCAL_PASSWORD}`)
    } catch (error) {
      console.error('Erro ao criar entrevistador:', error)
      toast.error('Erro ao criar entrevistador')
    }
  }

  const handleUpdateResearcher = async (researcherId: string, updates: Partial<Researcher>) => {
    try {
      const normalizedUpdates = {
        ...updates,
        ...(typeof updates.email === 'string' ? { email: normalizeEmail(updates.email) } : {}),
      }
      const updatedResearcher = await lumi.entities.researchers.update(researcherId, {
        ...normalizedUpdates,
        updatedAt: new Date().toISOString(),
      }) as Researcher

      setResearchers((prev) => sortResearchersByName(prev.map((item) => (
        item._id === researcherId ? { ...item, ...updatedResearcher } : item
      ))))
      setEditingResearcher(null)
      toast.success('Entrevistador atualizado com sucesso!')
    } catch (error) {
      console.error('Erro ao atualizar entrevistador:', error)
      toast.error('Erro ao atualizar entrevistador')
    }
  }

  const handleToggleStatus = async (researcher: Researcher) => {
    await handleUpdateResearcher(researcher._id, { isActive: !researcher.isActive })
  }

  const handleResetPassword = async (researcherId: string) => {
    if (!confirm(`Redefinir a senha deste entrevistador para ${DEFAULT_LOCAL_PASSWORD}?`)) {
      return
    }

    try {
      const credentials = await createLocalCredentials(DEFAULT_LOCAL_PASSWORD, {
        mustResetPassword: true,
      })
      const updatedResearcher = await lumi.entities.researchers.update(researcherId, {
        ...credentials,
        updatedAt: new Date().toISOString(),
      }) as Researcher

      setResearchers((prev) => sortResearchersByName(prev.map((item) => (
        item._id === researcherId ? { ...item, ...updatedResearcher } : item
      ))))
      toast.success(`Senha redefinida para ${DEFAULT_LOCAL_PASSWORD}.`)
    } catch (error) {
      console.error('Erro ao redefinir senha:', error)
      toast.error('Erro ao redefinir senha')
    }
  }

  const handleDeleteResearcher = async (researcher: Researcher) => {
    if (!confirm(`Tem certeza que deseja excluir o entrevistador "${researcher.name}"? Esta ação não pode ser desfeita.`)) {
      return
    }

    try {
      await lumi.entities.researchers.delete(researcher._id)
      setResearchers((prev) => prev.filter((item) => item._id !== researcher._id))
      toast.success('Entrevistador excluído com sucesso!')
    } catch (error) {
      console.error('Erro ao excluir entrevistador:', error)
      toast.error('Erro ao excluir entrevistador')
    }
  }

  const filteredResearchers = useMemo(() => {
    const query = searchTerm.trim().toLowerCase()

    return researchers.filter((researcher) => {
      const matchesSearch =
        !query ||
        researcher.name.toLowerCase().includes(query) ||
        researcher.email.toLowerCase().includes(query) ||
        (researcher.department || '').toLowerCase().includes(query)
      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'active' && researcher.isActive) ||
        (statusFilter === 'inactive' && !researcher.isActive)

      return matchesSearch && matchesStatus
    })
  }, [researchers, searchTerm, statusFilter])

  const researchersTotalPages = Math.max(1, Math.ceil(filteredResearchers.length / 10))
  const researchersCurrentPage = Math.min(researchersPage, researchersTotalPages)
  const researchersPageItems = filteredResearchers.slice((researchersCurrentPage - 1) * 10, researchersCurrentPage * 10)

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-3xl font-bold text-gray-900">Entrevistadores</h1>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center space-x-2 rounded-lg bg-blue-600 px-4 py-2 font-medium text-white transition-colors hover:bg-blue-700"
        >
          <Plus size={20} />
          <span>Novo Entrevistador</span>
        </button>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-4 sm:flex-row">
          <div className="relative flex-1">
            <Search size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar entrevistador..."
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              className="w-full rounded-lg border border-gray-300 py-2 pl-10 pr-4 focus:border-transparent focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex items-center space-x-2">
            <Filter size={20} className="text-gray-400" />
            <select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
              className="rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Todos os Status</option>
              <option value="active">Ativos</option>
              <option value="inactive">Inativos</option>
            </select>
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="grid grid-cols-[minmax(0,1fr)_140px_120px] gap-3 border-b border-gray-200 bg-gray-50 px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
          <span>Nome</span>
          <span>Situação</span>
          <span className="text-right">Ações</span>
        </div>

        {filteredResearchers.length === 0 ? (
          <div className="py-12 text-center">
            <UserCheck size={44} className="mx-auto mb-3 text-gray-400" />
            <h3 className="text-lg font-medium text-gray-900">
              {searchTerm || statusFilter !== 'all' ? 'Nenhum entrevistador encontrado' : 'Nenhum entrevistador cadastrado'}
            </h3>
            <p className="mt-1 text-gray-500">
              {searchTerm || statusFilter !== 'all'
                ? 'Tente ajustar os filtros de busca.'
                : 'Cadastre o primeiro entrevistador para iniciar.'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {researchersPageItems.map((researcher) => (
              <button
                key={researcher._id}
                type="button"
                onClick={() => setEditingResearcher(researcher)}
                className="grid w-full grid-cols-[minmax(0,1fr)_140px_120px] items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-blue-50/60"
              >
                <span className="truncate text-sm font-medium text-gray-900">{researcher.name}</span>
                <span>
                  <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${
                    researcher.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {researcher.isActive ? 'Ativo' : 'Inativo'}
                  </span>
                </span>
                <span className="flex justify-end gap-1" onClick={(event) => event.stopPropagation()}>
                  <button
                    type="button"
                    onClick={() => setEditingResearcher(researcher)}
                    className="rounded-lg p-2 text-blue-600 hover:bg-blue-100"
                    title="Abrir cadastro completo"
                  >
                    <Edit size={16} />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleToggleStatus(researcher)}
                    className={`rounded-lg p-2 ${researcher.isActive ? 'text-red-600 hover:bg-red-100' : 'text-green-600 hover:bg-green-100'}`}
                    title={researcher.isActive ? 'Desativar' : 'Ativar'}
                  >
                    {researcher.isActive ? <UserX size={16} /> : <UserCheck size={16} />}
                  </button>
                </span>
              </button>
            ))}
          </div>
        )}
        {filteredResearchers.length > 0 && (
          <div className="mt-4 flex flex-col items-center justify-between gap-3 border-t border-gray-100 pt-4 text-sm text-gray-600 sm:flex-row">
            <span>
              Pagina {researchersCurrentPage} de {researchersTotalPages} · {filteredResearchers.length} registro(s)
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={researchersCurrentPage <= 1}
                onClick={() => setResearchersPage(researchersCurrentPage - 1)}
                className="rounded-lg border border-gray-300 px-3 py-1.5 font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Anterior
              </button>
              <button
                type="button"
                disabled={researchersCurrentPage >= researchersTotalPages}
                onClick={() => setResearchersPage(researchersCurrentPage + 1)}
                className="rounded-lg border border-gray-300 px-3 py-1.5 font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Proxima
              </button>
            </div>
          </div>
        )}
      </div>

      {(showCreateModal || editingResearcher) && (
        <ResearcherFormModal
          researcher={editingResearcher}
          onSave={editingResearcher
            ? (data) => handleUpdateResearcher(editingResearcher._id, data)
            : handleCreateResearcher}
          onResetPassword={editingResearcher ? () => handleResetPassword(editingResearcher._id) : undefined}
          onDelete={editingResearcher ? () => handleDeleteResearcher(editingResearcher) : undefined}
          onClose={() => {
            setShowCreateModal(false)
            setEditingResearcher(null)
          }}
        />
      )}
    </div>
  )
}

const ResearcherFormModal: React.FC<{
  researcher?: Researcher | null
  onSave: (data: Partial<Researcher>) => void
  onResetPassword?: () => void
  onDelete?: () => void
  onClose: () => void
}> = ({ researcher, onSave, onResetPassword, onDelete, onClose }) => {
  const [formData, setFormData] = useState({
    name: researcher?.name || '',
    email: researcher?.email || '',
    phone: researcher?.phone || '',
    department: researcher?.department || '',
    specialization: researcher?.specialization || '',
    photoUrl: researcher?.photoUrl || '',
    isActive: researcher?.isActive ?? true,
  })
  const [uploadingPhoto, setUploadingPhoto] = useState(false)

  const handlePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    if (!file.type.startsWith('image/')) {
      toast.error('Selecione apenas arquivos de imagem.')
      return
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('A foto deve ter no máximo 5MB.')
      return
    }

    try {
      setUploadingPhoto(true)
      const result = await lumi.tools.file.upload([file])
      if (result[0]?.fileUrl) {
        setFormData((prev) => ({ ...prev, photoUrl: result[0].fileUrl! }))
        toast.success('Foto enviada com sucesso!')
      } else if (result[0]?.uploadError) {
        toast.error(`Erro ao enviar foto: ${result[0].uploadError}`)
      }
    } catch (error) {
      console.error('Erro ao enviar foto:', error)
      toast.error('Erro ao enviar foto')
    } finally {
      setUploadingPhoto(false)
      event.target.value = ''
    }
  }

  const handleSubmit = (event: React.FormEvent | React.MouseEvent) => {
    event.preventDefault()

    onSave({
      name: formData.name.trim(),
      email: formData.email.trim(),
      phone: formData.phone.trim() || undefined,
      department: formData.department.trim() || undefined,
      specialization: formData.specialization.trim() || undefined,
      photoUrl: formData.photoUrl || undefined,
      isActive: formData.isActive,
    })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <h2 className="text-2xl font-bold text-gray-900">
            {researcher ? 'Cadastro do Entrevistador' : 'Novo Entrevistador'}
          </h2>
          <button type="button" onClick={onClose} className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          <div className="mb-6 flex flex-col gap-4 rounded-xl border border-gray-200 bg-gray-50 p-4 sm:flex-row sm:items-center">
            <div className="flex h-28 w-28 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-gray-200 bg-white">
              {formData.photoUrl ? (
                <img src={formData.photoUrl} alt={formData.name || 'Foto do entrevistador'} className="h-full w-full object-cover" />
              ) : (
                <UserCheck size={34} className="text-gray-300" />
              )}
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-gray-900">Foto do entrevistador</p>
              <p className="mt-1 text-sm text-gray-500">A foto aparece somente ao abrir o cadastro completo.</p>
              <label className="mt-3 inline-flex cursor-pointer items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700">
                <Upload size={16} />
                {uploadingPhoto ? 'Enviando...' : 'Enviar foto'}
                <input type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" disabled={uploadingPhoto} />
              </label>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Nome Completo *</label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(event) => setFormData((prev) => ({ ...prev, name: event.target.value }))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500"
                placeholder="Digite o nome completo"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">E-mail *</label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(event) => setFormData((prev) => ({ ...prev, email: event.target.value }))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500"
                placeholder="Digite o e-mail"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Telefone</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(event) => setFormData((prev) => ({ ...prev, phone: event.target.value }))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500"
                placeholder="(11) 99999-9999"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Departamento</label>
              <input
                type="text"
                value={formData.department}
                onChange={(event) => setFormData((prev) => ({ ...prev, department: event.target.value }))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500"
                placeholder="Ex: Marketing, Vendas, TI"
              />
            </div>
          </div>

          <div className="mt-5">
            <label className="mb-2 block text-sm font-medium text-gray-700">Especialização</label>
            <textarea
              rows={3}
              value={formData.specialization}
              onChange={(event) => setFormData((prev) => ({ ...prev, specialization: event.target.value }))}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500"
              placeholder="Descreva as áreas de especialização e experiência"
            />
          </div>

          <div className="mt-5 flex items-center">
            <input
              type="checkbox"
              id="isActive"
              checked={formData.isActive}
              onChange={(event) => setFormData((prev) => ({ ...prev, isActive: event.target.checked }))}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label htmlFor="isActive" className="ml-2 text-sm text-gray-700">Entrevistador ativo</label>
          </div>

          {researcher && (
            <div className="mt-6 grid grid-cols-1 gap-3 rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600 sm:grid-cols-2">
              <div className="flex items-center gap-2"><Mail size={16} /> {researcher.email}</div>
              {researcher.phone && <div className="flex items-center gap-2"><Phone size={16} /> {researcher.phone}</div>}
              {researcher.department && <div className="flex items-center gap-2"><FileText size={16} /> {researcher.department}</div>}
              <div className="flex items-center gap-2"><Calendar size={16} /> Desde {new Date(researcher.createdAt).toLocaleDateString('pt-BR')}</div>
            </div>
          )}

          <div className="mt-6 rounded-lg border border-blue-100 bg-blue-50 p-4 text-sm text-blue-800">
            <div className="flex items-start gap-3">
              <KeyRound size={18} className="mt-0.5 shrink-0" />
              <div>
                <p className="font-medium">Acesso por e-mail e senha local</p>
                <p className="mt-1">
                  Novos entrevistadores recebem a senha padrão {DEFAULT_LOCAL_PASSWORD} e devem redefinir a senha no primeiro acesso.
                </p>
              </div>
            </div>
          </div>
        </form>

        <div className="flex flex-col gap-3 border-t border-gray-200 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap gap-2">
            {onResetPassword && (
              <button type="button" onClick={onResetPassword} className="inline-flex items-center gap-2 rounded-lg border border-amber-300 px-4 py-2 text-sm font-medium text-amber-700 hover:bg-amber-50">
                <RotateCcw size={16} />
                Resetar senha
              </button>
            )}
            {onDelete && (
              <button type="button" onClick={onDelete} className="inline-flex items-center gap-2 rounded-lg border border-red-300 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50">
                <Trash2 size={16} />
                Excluir
              </button>
            )}
          </div>
          <div className="flex justify-end gap-3">
            <button type="button" onClick={onClose} className="rounded-lg border border-gray-300 px-6 py-2 text-gray-700 hover:bg-gray-50">
              Cancelar
            </button>
            <button type="button" onClick={handleSubmit} className="rounded-lg bg-blue-600 px-6 py-2 text-white hover:bg-blue-700">
              {researcher ? 'Atualizar' : 'Cadastrar'} Entrevistador
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ResearcherManager